import { useState, useEffect } from 'react';
import { X, Sparkles } from 'lucide-react';
import { Button, Input, Alert, Select } from '@dreamtree-org/twreact-ui';
import { useAppApi } from '@/lib/api';
import { useChatDock } from '@/lib/ChatDockContext';
import { slugify } from '@/lib/slug';
import { canonicalGraph, STARTER_GRAPH } from '@/lib/workflowGraph';

/**
 * "New workflow" — name and description only.
 *
 * The old flow dropped you straight into the canvas with a Key field to invent yourself.
 * `key` is the tool name agents call the workflow by: immutable after create and unique per
 * company, so getting it wrong meant a 400 AFTER authoring a whole graph. It is now derived
 * server-side (models/AppWorkflow.model.js's _deriveFreeKey), and the row is created before
 * the editor opens — so the editor only ever edits a workflow that exists.
 *
 * Overlay markup follows components/ToolTraceStack.jsx's TraceModal, the app's one working
 * modal, rather than twreact's unused Dialog.
 */

const overlayClass = 'fixed inset-0 z-[60] bg-black/40 flex items-center justify-center p-4';
const panelClass = 'bg-[var(--app-surface)] rounded-lg shadow-xl w-full max-w-lg flex flex-col';
const textareaClass = 'w-full border rounded-md px-2 py-1.5 text-sm bg-[var(--app-surface)]';

const errText = (ex) => (ex.response && ex.response.data && ex.response.data.error) || ex.message;

/**
 * The starter LLM-app spec — the smallest spec that compiles (validateLlmAppSpec requires
 * a user_prompt; everything else is optional). The LLM App tab in the builder is where the
 * real spec gets authored; this just gives the run a body on day one.
 */
function starterLlmAppSpec({ name, description }) {
  return JSON.stringify({
    mode: 'llm',
    system_prompt: description ? `You are ${name.trim()}.` : undefined,
    user_prompt: description || `Do the task "${name.trim()}" using the run input.`,
    input: [{ name: 'text', type: 'string', optional: true, description: 'Optional free text the prompt can reference as {{input.text}}' }],
  }, null, 2);
}

/** The Kind pick — visual graph, or a declarative LLM App compiled from a spec. */
function KindField({ kind, onChange }) {
  return (
    <>
      <Select
        label="Kind"
        options={[
          { label: 'Workflow (visual graph)', value: 'canvas' },
          { label: 'LLM App — intelligence endpoint (spec)', value: 'llm_app' },
        ]}
        value={kind}
        onChange={onChange}
      />
      {kind === 'llm_app' && (
        <div className="text-xs text-secondary-500">
          Compiles to input → LLM → output from a declarative spec (prompts, input/output
          schemas, client_tools executed by the calling app). Third parties run it with
          POST /api/v1/workflows/&#123;key&#125;/run — edit the spec on the builder's LLM App tab.
        </div>
      )}
    </>
  );
}

export default function NewWorkflowDialog({ onClose, onCreated }) {
  const { fetchApi } = useAppApi();
  const { openDockWithAgent } = useChatDock() || {};
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [kind, setKind] = useState('canvas');
  const [withArchitect, setWithArchitect] = useState(false);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');
  const [apps, setApps] = useState([]);
  const [applicationId, setApplicationId] = useState(null);

  useEffect(() => {
    fetchApi('applicationsList').then((res) => setApps(Array.isArray(res) ? res : [])).catch(() => {});
  }, []);

  // A preview, not the value. Only the server can see which keys are already taken, so it
  // may hand back `draft-reply-bot-2` — the editor shows whatever was actually stored.
  const keyPreview = slugify(name, 'workflow');

  const create = async () => {
    setSaving(true);
    setErr('');
    try {
      const llmApp = kind === 'llm_app';
      const data = {
        name: name.trim(),
        description: description.trim() || null,
        is_active: true,
        application_id: applicationId || null,
      };
      if (llmApp) data.spec_json = starterLlmAppSpec({ name, description });
      else data.graph_json = JSON.stringify(canonicalGraph(STARTER_GRAPH.nodes, STARTER_GRAPH.edges));
      const created = await fetchApi('crud', {
        model: 'AppWorkflow',
        action: 'create',
        data,
      });
      // korm's create response comes back in three different shapes across this app;
      // reading `created.id` alone silently navigates to /app-workflow/undefined.
      const newId = created?.data?.id ?? created?.[0]?.id ?? created?.id;
      if (!newId) throw new Error('Created the workflow, but could not read its new id back from the response.');
      // The dock is mounted in DefaultLayout and survives the navigation, so opening it
      // here means the builder loads with Workflow Architect already waiting.
      if (withArchitect && openDockWithAgent) openDockWithAgent('workflow_architect');
      onCreated(newId);
    } catch (ex) {
      setErr(errText(ex));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className={overlayClass} onClick={onClose}>
      <div className={panelClass} onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-4 py-3 border-b border-[var(--app-border)]">
          <span className="font-semibold text-sm">New workflow</span>
          <button type="button" onClick={onClose} aria-label="Close" className="p-1 rounded hover:bg-[var(--app-border)]/40">
            <X size={16} />
          </button>
        </div>

        <div className="px-4 py-4 space-y-3">
          {err && <Alert variant="error" title="Could not create">{err}</Alert>}

          <Input
            label="Name"
            value={name}
            autoFocus
            placeholder="e.g. Draft reply bot"
            onChange={(e) => setName(e.target.value)}
          />

          <KindField kind={kind} onChange={setKind} />

          <label className="block space-y-1">
            <span className="text-sm">Description</span>
            <textarea
              className={textareaClass}
              rows={3}
              value={description}
              placeholder="What this workflow does, and when an agent should run it."
              onChange={(e) => setDescription(e.target.value)}
            />
          </label>

          <Select
            label="Application"
            options={[{ label: 'Company-wide (all apps)', value: '' }, ...apps.map(a => ({ label: a.name, value: a.id }))]}
            value={applicationId || ''}
            onChange={(v) => setApplicationId(v || null)}
            allowClear
            searchable
          />

          <div className="text-xs text-secondary-500">
            Key: <code className="font-mono">{name.trim() ? keyPreview : '—'}</code>
            <span className="ml-1">— generated automatically, and what agents call this workflow by.</span>
          </div>

          {openDockWithAgent && kind === 'canvas' && (
            <label className="flex items-start gap-2 text-sm rounded-md border border-[var(--app-border)] p-2">
              <input
                type="checkbox"
                className="mt-1"
                checked={withArchitect}
                onChange={(e) => setWithArchitect(e.target.checked)}
              />
              <span>
                <span className="flex items-center gap-1 font-medium">
                  <Sparkles size={13} /> Draft it with Workflow Architect
                </span>
                <span className="block text-xs text-secondary-500">
                  Opens the assistant on the builder. Describe the steps above and ask it to draft the
                  graph — nothing it proposes is saved until you review and Save.
                </span>
              </span>
            </label>
          )}
        </div>

        <div className="flex items-center justify-end gap-2 px-4 py-3 border-t border-[var(--app-border)]">
          <Button size="sm" variant="secondary" onClick={onClose}>Cancel</Button>
          <Button size="sm" onClick={create} disabled={saving || !name.trim()}>
            {saving ? 'Creating…' : 'Create workflow'}
          </Button>
        </div>
      </div>
    </div>
  );
}
